import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  FileText, ClipboardList, Search, ChevronDown, ChevronUp,
  User, Calendar, Settings, Users, Link, Vote, BookOpen,
  Filter, X, ArrowUpDown, Clock, UserCircle, RefreshCw
} from 'lucide-react';
import { ACTION_LABELS, ACTION_COLORS } from '@/lib/helpers';

const FIELD_LABELS = {
  nome_completo: 'Nome', genero: 'Género', data_nascimento: 'Data de nascimento',
  email: 'E-mail', whatsapp: 'WhatsApp', whatsapp_pai: 'WhatsApp do Pai',
  whatsapp_mae: 'WhatsApp da Mãe', batizado: 'Batizado', selado: 'Selado',
  nome_pai: 'Nome do Pai', nome_mae: 'Nome da Mãe', status: 'Status',
  musico: 'Músico', organista: 'Organista', aluno_gem: 'Aluno GEM',
  auxiliar_jovens: 'Auxiliar de Jovens', grupo_visita: 'Grupo de Visita',
  instrutor_gem: 'Instrutor GEM', instrutora_musicalizacao: 'Instrutora de Musicalização',
  integrante_ebi: 'Integrante EBI', instrumentos: 'Instrumentos',
  fila_manual: 'Atribuição manual', name: 'Nome', date: 'Data',
  description: 'Descrição', visibilidade: 'Visibilidade', pessoa_nome: 'Pessoa associada',
  quem_presidiu: 'Quem presidiu', qtd_musicos: 'Qtd. de músicos',
  qtd_ministerio: 'Qtd. de ministério', qtd_participantes: 'Qtd. de participantes',
  atribuicoes: 'Atribuições', app_name: 'Nome do aplicativo', subtitle: 'Subtítulo',
  terms_of_use: 'Termo de uso', presente: 'Presença', recorrencia_ativa: 'Recorrência ativa',
  recorrencia_dia_semana: 'Dia da semana', recorrencia_horario: 'Horário',
  recorrencia_frequencia: 'Frequência', tem_presidencia: 'Tem presidência',
  tem_musicos: 'Tem músicos', tem_ministerio: 'Tem ministério',
  tem_participantes: 'Tem participantes', tem_recitativos: 'Tem recitativos',
  permite_pessoa: 'Permite associar pessoa', nome: 'Nome', tipo: 'Tipo',
  prazo_fechamento_horas: 'Prazo de fechamento (horas)', pergunta: 'Pergunta',
  respondida: 'Respondida', titulo: 'Título', url: 'URL', ativo: 'Ativo',
  motivo: 'Motivo', telefone: 'Telefone',
};

const HIDDEN_FIELDS = new Set([
  'pessoa_id', 'event_id', 'church_id', 'created_at', 'created_by',
  'closed_at', 'total_pessoas_snapshot', 'tipo_evento_id', 'visita_id',
  '_id', 'approved_at', 'approved_by', 'attendance_id', 'auxiliar_id',
  'user_id', 'marked_at', 'marked_by', 'log_id', 'timestamp',
  'auto_recorrencia', 'reuniao_linked', 'deleted', 'deleted_at',
  'fila_id', 'apoio_auxiliar_id', 'recitativos', 'manual_reopen',
]);

const ENTITY_LABELS = {
  pessoa: 'Pessoa', event: 'Evento', auxiliar: 'Auxiliar',
  attendance: 'Presença', settings: 'Configurações', fila: 'Atribuição',
  tipo_evento: 'Tipo de Evento', reuniao: 'Reunião', church: 'Igreja',
  link: 'Link', votacao: 'Votação', anotacao: 'Anotação',
  pergunta: 'Pergunta', visita: 'Visita', motivo_inativacao: 'Motivo Inativação',
  user: 'Utilizador', admin: 'Admin',
};

const ENTITY_ICONS = {
  pessoa: <User className="w-3.5 h-3.5" />,
  event: <Calendar className="w-3.5 h-3.5" />,
  auxiliar: <Users className="w-3.5 h-3.5" />,
  attendance: <ClipboardList className="w-3.5 h-3.5" />,
  settings: <Settings className="w-3.5 h-3.5" />,
  fila: <ArrowUpDown className="w-3.5 h-3.5" />,
  tipo_evento: <Calendar className="w-3.5 h-3.5" />,
  reuniao: <BookOpen className="w-3.5 h-3.5" />,
  church: <Settings className="w-3.5 h-3.5" />,
  link: <Link className="w-3.5 h-3.5" />,
  votacao: <Vote className="w-3.5 h-3.5" />,
  anotacao: <FileText className="w-3.5 h-3.5" />,
  pergunta: <FileText className="w-3.5 h-3.5" />,
  visita: <User className="w-3.5 h-3.5" />,
};

const CATEGORY_MAP = {
  created: 'Criação', updated: 'Atualização', deleted: 'Exclusão',
  approved: 'Aprovação', rejected: 'Rejeição', closed: 'Fechamento',
  toggled: 'Alternado', imported: 'Importação', impersonate: 'Impersonar',
};

function formatValue(val) {
  if (val === null || val === undefined || val === '') return '-';
  if (val === true) return 'Sim';
  if (val === false) return 'Não';
  if (Array.isArray(val)) return val.length === 0 ? '-' : val.join(', ');
  const str = String(val);
  const map = {
    masculino: 'Masculino', feminino: 'Feminino', pendente: 'Pendente',
    aprovado: 'Aprovado', rejeitado: 'Rejeitado', aberto: 'Aberto',
    fechado: 'Fechado', privado: 'Privado', auxiliares: 'Auxiliares',
    publico: 'Público', agendada: 'Agendada', em_andamento: 'Em andamento',
    concluida: 'Concluída', semanal: 'Semanal', quinzenal: 'Quinzenal',
    inativo: 'Inativo',
  };
  return map[str] || str;
}

function getEntityName(log) {
  const d = log.dados_depois || log.dados_antes || {};
  if (log.entity_type === 'attendance') {
    const nome = d.pessoa_nome || '';
    const evento = d.event_name || '';
    const status = d.presente ? 'Presente' : 'Ausente';
    return evento ? `${nome} - ${status} em "${evento}"` : `${nome} - ${status}`;
  }
  if (log.entity_type === 'pessoa') return d.nome_completo || '';
  if (log.entity_type === 'settings') return 'Configurações gerais';
  return d.name || d.nome || d.nome_completo || d.titulo || d.pergunta || '';
}

function getChangedFields(antes, depois) {
  if (!antes || !depois) return [];
  const changes = [];
  const allKeys = new Set([...Object.keys(antes), ...Object.keys(depois)]);
  for (const key of allKeys) {
    if (HIDDEN_FIELDS.has(key)) continue;
    if (JSON.stringify(antes[key]) !== JSON.stringify(depois[key])) {
      changes.push({ field: key, before: antes[key], after: depois[key] });
    }
  }
  return changes;
}

function getAllFields(dados) {
  if (!dados || typeof dados !== 'object') return [];
  return Object.entries(dados)
    .filter(([k]) => !HIDDEN_FIELDS.has(k))
    .map(([k, v]) => ({ field: k, value: v }));
}

function getActionColor(action) {
  for (const [key, cls] of Object.entries(ACTION_COLORS)) {
    if (action.includes(key)) return cls;
  }
  return 'bg-slate-100 text-slate-700';
}

function getActionCategory(action) {
  for (const [key, label] of Object.entries(CATEGORY_MAP)) {
    if (action.includes(key)) return label;
  }
  return 'Outro';
}

function AuditLogItem({ log }) {
  const [expanded, setExpanded] = useState(false);
  const label = ACTION_LABELS[log.action] || log.action;
  const entityName = getEntityName(log);
  const colorCls = getActionColor(log.action);
  const isUpdate = log.action.includes('updated') || log.action === 'pessoa_approved' || log.action === 'pessoa_rejected' || log.action === 'pending_update_approved' || log.action === 'pending_update_rejected';
  const changes = isUpdate ? getChangedFields(log.dados_antes, log.dados_depois) : [];
  const entityLabel = ENTITY_LABELS[log.entity_type] || log.entity_type;
  const entityIcon = ENTITY_ICONS[log.entity_type] || <FileText className="w-3.5 h-3.5" />;
  const allBefore = getAllFields(log.dados_antes);
  const allAfter = getAllFields(log.dados_depois);

  return (
    <div className={`border border-slate-100 rounded-lg mb-2 overflow-hidden transition-all ${expanded ? 'bg-white shadow-sm' : 'bg-white hover:bg-slate-50/50'}`} data-testid={`audit-log-${log.log_id}`}>
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${colorCls}`}>
          {entityIcon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`text-[10px] px-1.5 py-0 ${colorCls}`}>{label}</Badge>
            {entityName && <span className="text-sm font-medium text-slate-800 truncate">{entityName}</span>}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <Clock className="w-3 h-3 text-slate-400" />
            <span className="text-xs text-slate-400">{new Date(log.timestamp + (log.timestamp.endsWith('Z') ? '' : 'Z')).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', ' às')}</span>
            <span className="text-xs text-slate-300">|</span>
            <UserCircle className="w-3 h-3 text-slate-400" />
            <span className="text-xs text-slate-400">{log.user_name}</span>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </div>

      {expanded && (
        <div className="px-4 pb-3 border-t border-slate-100 pt-3" style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Tipo</p>
              <p className="text-xs font-medium text-slate-700">{entityLabel}</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">ID Registro</p>
              <p className="text-xs font-mono text-slate-600 truncate">{log.entity_id}</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">ID Log</p>
              <p className="text-xs font-mono text-slate-600 truncate">{log.log_id}</p>
            </div>
          </div>

          {changes.length > 0 && (
            <details className="group border border-slate-200 rounded-lg overflow-hidden">
              <summary className="flex items-center gap-2 px-3 py-2 bg-slate-50 cursor-pointer text-xs font-semibold text-slate-700 select-none list-none">
                <ChevronDown className="w-3.5 h-3.5 transition-transform group-open:rotate-180" />
                O que mudou
                <Badge className="bg-slate-200 text-slate-600 text-[10px] px-1.5 py-0 ml-auto">{changes.length}</Badge>
              </summary>
              <div className="p-2.5 space-y-1 bg-white">
                {changes.map((c, i) => (
                  <div key={i} className="text-xs text-slate-600 flex items-start gap-1">
                    <span className="font-medium text-slate-700 flex-shrink-0">{FIELD_LABELS[c.field] || c.field}:</span>
                    <span className="text-red-500/80 line-through">{formatValue(c.before)}</span>
                    <span className="text-slate-400">&rarr;</span>
                    <span className="text-green-600 font-medium">{formatValue(c.after)}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {allBefore.length > 0 && (
            <details className="group border border-slate-200 rounded-lg overflow-hidden">
              <summary className="flex items-center gap-2 px-3 py-2 bg-slate-50 cursor-pointer text-xs font-semibold text-slate-600 select-none list-none">
                <ChevronDown className="w-3.5 h-3.5 transition-transform group-open:rotate-180" />
                {isUpdate ? 'Dados antes' : 'Dados anteriores'}
                <Badge className="bg-slate-200 text-slate-600 text-[10px] px-1.5 py-0 ml-auto">{allBefore.length}</Badge>
              </summary>
              <div className="p-2.5 space-y-0.5 bg-white max-h-40 overflow-y-auto">
                {allBefore.map((item, i) => (
                  <div key={i} className="text-xs text-slate-500">
                    <span className="font-medium text-slate-600">{FIELD_LABELS[item.field] || item.field}:</span> {formatValue(item.value)}
                  </div>
                ))}
              </div>
            </details>
          )}

          {allAfter.length > 0 && (
            <details className="group border border-slate-200 rounded-lg overflow-hidden">
              <summary className="flex items-center gap-2 px-3 py-2 bg-slate-50 cursor-pointer text-xs font-semibold text-slate-600 select-none list-none">
                <ChevronDown className="w-3.5 h-3.5 transition-transform group-open:rotate-180" />
                {isUpdate ? 'Dados depois' : 'Dados do registro'}
                <Badge className="bg-slate-200 text-slate-600 text-[10px] px-1.5 py-0 ml-auto">{allAfter.length}</Badge>
              </summary>
              <div className="p-2.5 space-y-0.5 bg-white max-h-40 overflow-y-auto">
                {allAfter.map((item, i) => (
                  <div key={i} className="text-xs text-slate-500">
                    <span className="font-medium text-slate-600">{FIELD_LABELS[item.field] || item.field}:</span> {formatValue(item.value)}
                  </div>
                ))}
              </div>
            </details>
          )}

          {allBefore.length === 0 && allAfter.length === 0 && changes.length === 0 && null}
        </div>
      )}
    </div>
  );
}

export default function AuditoriaTab({ auditLogs, onRefresh, noCard = false }) {
  const [search, setSearch] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const uniqueEntities = useMemo(() => {
    const set = new Set(auditLogs.map(l => l.entity_type));
    return [...set].sort();
  }, [auditLogs]);

  const uniqueUsers = useMemo(() => {
    const set = new Set(auditLogs.map(l => l.user_name).filter(Boolean));
    return [...set].sort();
  }, [auditLogs]);

  const uniqueCategories = useMemo(() => {
    const set = new Set(auditLogs.map(l => getActionCategory(l.action)));
    return [...set].sort();
  }, [auditLogs]);

  const hasActiveFilters = entityFilter !== 'all' || categoryFilter !== 'all' || userFilter !== 'all';

  const filtered = useMemo(() => {
    let result = auditLogs;
    if (entityFilter !== 'all') result = result.filter(l => l.entity_type === entityFilter);
    if (categoryFilter !== 'all') result = result.filter(l => getActionCategory(l.action) === categoryFilter);
    if (userFilter !== 'all') result = result.filter(l => l.user_name === userFilter);
    if (search) {
      const term = search.toLowerCase();
      result = result.filter(l => {
        const name = getEntityName(l).toLowerCase();
        const lbl = (ACTION_LABELS[l.action] || l.action).toLowerCase();
        const userName = (l.user_name || '').toLowerCase();
        const entId = (l.entity_id || '').toLowerCase();
        return name.includes(term) || lbl.includes(term) || userName.includes(term) || entId.includes(term);
      });
    }
    return result;
  }, [auditLogs, entityFilter, categoryFilter, userFilter, search]);

  const clearFilters = () => {
    setEntityFilter('all');
    setCategoryFilter('all');
    setUserFilter('all');
    setSearch('');
  };

  const WrapperTag = noCard ? 'div' : Card;
  const HeaderTag = noCard ? 'div' : CardHeader;
  const ContentTag = noCard ? 'div' : CardContent;

  return (
    <WrapperTag className={noCard ? '' : 'bg-white border-0 shadow-sm'}>
      <HeaderTag className={noCard ? 'pb-3' : 'pb-3'}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Auditoria</h2>
              <p className="text-sm text-slate-500">
                {filtered.length === auditLogs.length
                  ? `${auditLogs.length} registros`
                  : `${filtered.length} de ${auditLogs.length} registros`
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-700 gap-1 h-8" data-testid="audit-clear-filters">
                <X className="w-3 h-3" /> Limpar
              </Button>
            )}
            {onRefresh && (
              <Button variant="outline" size="icon" className="h-8 w-8 bg-white" onClick={onRefresh} data-testid="audit-refresh-btn">
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="gap-1.5 h-8 text-xs bg-white" data-testid="audit-toggle-filters">
              <Filter className="w-3.5 h-3.5" /> Filtros
              {hasActiveFilters && <Badge className="bg-blue-600 text-white text-[10px] px-1 py-0 ml-0.5">{[entityFilter, categoryFilter, userFilter].filter(f => f !== 'all').length}</Badge>}
            </Button>
          </div>
        </div>

        <div className="relative mt-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white border-slate-200 h-9 text-sm" data-testid="audit-search" />
        </div>

        {showFilters && (
          <div className="grid grid-cols-3 gap-3 mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="audit-filters-panel">
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1 block">Módulo</label>
              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger className="h-8 text-xs bg-white" data-testid="audit-filter-entity">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {uniqueEntities.map(e => (
                    <SelectItem key={e} value={e}>{ENTITY_LABELS[e] || e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1 block">Tipo de ação</label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="h-8 text-xs bg-white" data-testid="audit-filter-category">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {uniqueCategories.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1 block">Utilizador</label>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="h-8 text-xs bg-white" data-testid="audit-filter-user">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {uniqueUsers.map(u => (
                    <SelectItem key={u} value={u}>{u}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </HeaderTag>
      <ContentTag className={noCard ? '' : 'pt-0'}>
        <div data-testid="audit-logs-list">
          {filtered.map((log) => (
            <AuditLogItem key={log.log_id} log={log} />
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-12">
              <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">{search || hasActiveFilters ? 'Nenhum resultado com os filtros aplicados' : 'Nenhum log registrado'}</p>
              {hasActiveFilters && (
                <Button variant="link" size="sm" onClick={clearFilters} className="text-blue-600 mt-1 text-xs">Limpar filtros</Button>
              )}
            </div>
          )}
        </div>
      </ContentTag>
    </WrapperTag>
  );
}
